import type { FC } from 'react'
import { useState } from 'react'
import { useFormik } from 'formik'
import ClearIcon from '@mui/icons-material/Clear'
import {
  Select,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  FormLabel,
} from '@mui/material'
import { ProviderDetailWithAdditionalData } from './ProviderListItem'
import { Loader, PhoneNumberField, isIncompletePhoneNumber } from '~/shared/ui'
import { EditCuratedProviderPayload, useEditCuratedProvider } from './utils'
import { EditProviderAndAvailabilityPayload, logEvent } from '~/shared/lib/events'
import { required } from '~/shared/lib/required'

interface UpdateProviderDetailsModalProps {
  provider: ProviderDetailWithAdditionalData | null
  specialtyGroups: { id: number; label: string }[] | null
  handleEditProviderDetailsClose: () => void
  steerageId?: number | null
  providerSearchType: 'provider' | 'facility' | 'procedure'
  isEditedInDirectory?: boolean
}

const UpdateProviderDetailsModal: FC<UpdateProviderDetailsModalProps> = props => {
  const providerName = props?.provider?.name
  const facilityLabel = 'All providers at this location'
  const { isPending: isCuratedProviderUpdating, mutateAsync: handleEditCuratedProvider } =
    useEditCuratedProvider()

  const providerLabel = 'Only ' + providerName + ' at this location'
  const location =
    props?.provider?.locations && props?.provider?.locations.length >= 1
      ? props?.provider?.locations[0]
      : null

  const careOrgName = props?.provider?.npi ? location?.name : props?.provider?.name
  const [providerLevel, setProviderLevel] = useState<string>(
    props?.providerSearchType == 'provider' ? 'providerAtALocation' : 'allProvidersAtALocation',
  )
  const specialtyGroups: { id: number; label: string }[] | null = props?.specialtyGroups

  const [selectedSpecialtyGroup, setSelectedSpecialtyGroup] = useState<string | null>(
    specialtyGroups?.[0]?.label ?? '',
  )
  const hasLocation = Boolean(props?.provider?.locations && props?.provider?.locations?.length >= 1)
  const phoneNumbers = hasLocation ? props?.provider?.locations?.[0]?.phone_numbers : null
  const phoneNumber = phoneNumbers && phoneNumbers?.length > 0 ? phoneNumbers[0]?.phone : null

  const faxNumbers = hasLocation ? props?.provider?.locations?.[0]?.fax_numbers : null
  const fax = faxNumbers && faxNumbers?.length > 0 ? faxNumbers[0]?.fax : null

  const handleUpdateProviderDetailsChoice = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProviderLevel(event?.target?.value)
  }

  const [phoneValue, setPhoneValue] = useState<string | null>(phoneNumber ?? null)

  const [faxValue, setFaxValue] = useState<string | null>(fax ?? null)
  const hasIncompleteContactNumber =
    isIncompletePhoneNumber(phoneValue) || isIncompletePhoneNumber(faxValue)

  const canUpdateSpecialty =
    props?.providerSearchType == 'provider' &&
    careOrgName != null &&
    specialtyGroups != null &&
    specialtyGroups.length > 0

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // As we want to keep the verified checkbox unchecked by default
      // so set the default value of isVerified as false
      isVerified: false,
    },
    onSubmit: async values => {
      let specialtyGroup: { id: number; label: string } | null | undefined
      if (specialtyGroups?.length == 1) {
        specialtyGroup = specialtyGroups ? specialtyGroups[0] : null
      } else {
        specialtyGroup = specialtyGroups?.find(
          specialtyGroup => specialtyGroup?.label == String(selectedSpecialtyGroup),
        )
      }

      // Dual write the contact details in CuratedProviders and Firefly source data
      const curatedProviderPayload: EditCuratedProviderPayload = {
        npi: props?.provider?.npi ?? null,
        care_org_name: careOrgName ?? null,
        zip_code: location?.address_details?.zip ?? null,
        city: location?.address_details?.city ?? null,
        state: location?.address_details?.state ?? null,
        address_line_1: location?.address_details?.address_line_1 ?? null,
        address_line_2: location?.address_details?.address_line_2
          ? location?.address_details?.address_line_2
          : null,
        contact_information: {
          is_verified: values?.isVerified,
          phone: phoneValue,
          fax: faxValue,
          specialty_group:
            specialtyGroup?.id && providerLevel != 'providerAtALocation'
              ? specialtyGroup?.id
              : null,
        },
      }
      try {
        // Await so the mutation's state update lands while the modal is still
        // mounted (avoids "setState on unmounted component" after the close below).
        await handleEditCuratedProvider(curatedProviderPayload)
      } catch {
        // The mutation hook surfaces its own error notification; swallow here so
        // the modal still closes and the rejection isn't left unhandled.
      }
      if (providerLevel == 'providerAtALocation') {
        // Log provider contact details update for a provider at a location
        void logEvent<EditProviderAndAvailabilityPayload>(
          'UPDATED_PROVIDER_DETAILS_PROVIDER_AT_A_LOCATION',
          {
            providerNpi: props?.provider?.npi ?? null,
            steerageId: props?.steerageId,
            isEditedInDirectory: true,
            isVerified: values?.isVerified,
            isProviderSearch: true,
          },
        )
      } else if (providerLevel == 'allProvidersInDepartmentAtALocation') {
        // Log provider contact details update for all provider at a department
        void logEvent<EditProviderAndAvailabilityPayload>(
          'UPDATED_PROVIDER_DETAILS_ALL_PROVIDER_AT_A_DEPARTMENT',
          {
            careOrganisationName: careOrgName ?? '',
            steerageId: props?.steerageId,
            isEditedInDirectory: true,
            isVerified: values?.isVerified,
            isProviderSearch: props?.provider?.npi ? true : false,
          },
        )
      } else if (providerLevel == 'allProvidersAtALocation') {
        // Log provider contact details update for all provider at a location
        void logEvent<EditProviderAndAvailabilityPayload>(
          'UPDATED_PROVIDER_DETAILS_ALL_PROVIDER_AT_A_LOCATION',
          {
            careOrganisationName: careOrgName ?? '',
            steerageId: props?.steerageId,
            isEditedInDirectory: true,
            isVerified: values?.isVerified,
            isProviderSearch: false,
          },
        )
      }
      // close the model
      props.handleEditProviderDetailsClose()
    },
  })

  return (
    <Box>
      {isCuratedProviderUpdating ? <Loader /> : null}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 500, fontSize: '20px', lineHeight: '16px', mr: 4 }}>
          Edit Provider
        </Typography>
        <Box sx={{ mt: -1 }}>
          <IconButton size="small" onClick={props.handleEditProviderDetailsClose}>
            <ClearIcon />
          </IconButton>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '16px', mt: '-4px', mr: 4 }}>{props?.provider?.name}</Typography>
      <Box sx={{ mt: 3 }}>
        <PhoneNumberField
          name="phone"
          label="Phone"
          size="small"
          variant="standard"
          value={phoneValue}
          onChange={digits => setPhoneValue(digits || null)}
          sx={{ width: '75%' }}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <PhoneNumberField
          name="fax"
          label="Fax"
          size="small"
          variant="standard"
          value={faxValue}
          onChange={digits => setFaxValue(digits || null)}
          sx={{ width: '75%' }}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={<Checkbox name="isVerified" onChange={formik.handleChange} />}
          label="I have verified the phone and fax information"
        />
      </Box>
      <Box sx={{ mt: 3 }}>
        <FormControl>
          <FormLabel style={{ color: '#071F3E' }}>Edit details for:</FormLabel>

          <RadioGroup
            onChange={handleUpdateProviderDetailsChoice}
            defaultValue={
              props?.providerSearchType == 'provider'
                ? 'providerAtALocation'
                : 'allProvidersAtALocation'
            }
          >
            {props?.providerSearchType == 'provider' ? (
              <FormControlLabel
                control={<Radio />}
                onChange={formik.handleChange}
                label={providerLabel}
                value="providerAtALocation"
                name="providerLevel"
              />
            ) : (
              <FormControlLabel
                value="allProvidersAtALocation"
                name="providerLevel"
                onChange={formik.handleChange}
                control={<Radio />}
                label={facilityLabel}
              />
            )}
            {/* The editing at the specialty level should not be visible in the facility search */}
            {canUpdateSpecialty && specialtyGroups ? (
              <FormControlLabel
                control={<Radio />}
                name="providerLevel"
                value="allProvidersInDepartmentAtALocation"
                label={
                  <Grid container direction={'row'} spacing={0.5}>
                    <Grid>All</Grid>
                    {specialtyGroups.length > 1 ? (
                      <Grid>
                        <Select
                          variant="standard"
                          name="specialtyGroup"
                          sx={{ maxHeight: '25px', ml: '4px', mr: '5px' }}
                          value={selectedSpecialtyGroup}
                          onChange={e => {
                            setSelectedSpecialtyGroup(e.target.value)
                          }}
                        >
                          {specialtyGroups.map(group => (
                            <MenuItem value={group.label} key={group.id}>
                              {group.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    ) : (
                      <Grid>{required(specialtyGroups[0], 'the one specialty group').label}</Grid>
                    )}
                    <Grid>providers</Grid>
                    <Grid> at</Grid>
                    <Grid> this</Grid>
                    <Grid> location</Grid>
                  </Grid>
                }
              />
            ) : (
              ''
            )}
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          sx={{ mr: '10px', borderRadius: '28px' }}
          onClick={props.handleEditProviderDetailsClose}
        >
          <Typography>Cancel</Typography>
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => formik.handleSubmit()}
          disabled={hasIncompleteContactNumber}
          sx={{ borderRadius: '28px' }}
        >
          <Typography>Save</Typography>
        </Button>
      </Box>
    </Box>
  )
}

export default UpdateProviderDetailsModal
